/**
 * A history-API router in sixty lines (ADR 0009 §3).
 *
 * Five views do not justify a routing library, and the pure part — path in,
 * route out — is trivially unit-testable this way. The daemon serves
 * `index.html` for unknown non-API paths, which is what makes a deep link
 * survive a page reload.
 */

import { useEffect, useState } from 'react'
import type { Role } from '../api/types'

export type Route =
  | { view: 'dashboard' }
  | { view: 'models' }
  | { view: 'tools' }
  | { view: 'hours' }
  | { view: 'sessions' }
  | { view: 'projects' }
  | { view: 'quotas' }
  | { view: 'people' }
  | { view: 'events' }
  | { view: 'reconciliation' }
  | { view: 'sources' }
  | { view: 'data-quality' }
  | { view: 'budgets' }
  | { view: 'account' }

export type NavGroup = 'Overview' | 'Analysis' | 'Operations'
export interface NavEntry {
  readonly path: string
  readonly label: string
  readonly shortLabel: string
  readonly group: NavGroup
  readonly route: Route
  readonly roles?: readonly Role[]
}

export const NAV: readonly NavEntry[] = [
  { path: '/', label: 'Executive overview', shortLabel: 'Overview', group: 'Overview', route: { view: 'dashboard' } },
  { path: '/models', label: 'Model economics', shortLabel: 'Models', group: 'Analysis', route: { view: 'models' } },
  { path: '/tools', label: 'Provider and tool usage', shortLabel: 'Tools', group: 'Analysis', route: { view: 'tools' } },
  { path: '/hours', label: 'Usage by hour', shortLabel: 'Hours', group: 'Analysis', route: { view: 'hours' } },
  { path: '/sessions', label: 'Sessions', shortLabel: 'Sessions', group: 'Analysis', route: { view: 'sessions' }, roles: ['member', 'admin'] },
  { path: '/projects', label: 'Projects', shortLabel: 'Projects', group: 'Analysis', route: { view: 'projects' }, roles: ['member', 'admin'] },
  { path: '/people', label: 'People and plan value', shortLabel: 'People', group: 'Analysis', route: { view: 'people' }, roles: ['member', 'admin'] },
  { path: '/events', label: 'Event explorer', shortLabel: 'Events', group: 'Analysis', route: { view: 'events' }, roles: ['member', 'admin'] },
  { path: '/quotas', label: 'Quotas and balances', shortLabel: 'Quotas', group: 'Operations', route: { view: 'quotas' } },
  { path: '/reconciliation', label: 'Data reconciliation', shortLabel: 'Reconciliation', group: 'Operations', route: { view: 'reconciliation' } },
  { path: '/sources', label: 'Sources and coverage', shortLabel: 'Sources', group: 'Operations', route: { view: 'sources' }, roles: ['admin'] },
  { path: '/data-quality', label: 'Data quality', shortLabel: 'Data quality', group: 'Operations', route: { view: 'data-quality' }, roles: ['admin'] },
  { path: '/budgets', label: 'Budgets and alerts', shortLabel: 'Budgets', group: 'Operations', route: { view: 'budgets' }, roles: ['admin'] },
  { path: '/account', label: 'Account and privacy', shortLabel: 'Account', group: 'Operations', route: { view: 'account' } },
]

/**
 * Path → route. Unknown paths land on the dashboard rather than a 404 page:
 * every reachable URL here is same-origin and authenticated, so an unknown
 * one is a stale link, and the kindest answer is the front door.
 */
export function parsePath(pathname: string): Route {
  const clean = pathname.replace(/\/+$/, '') || '/'
  const hit = NAV.find((n) => n.path === clean)
  return hit ? hit.route : { view: 'dashboard' }
}

export function useRoute(): { route: Route; navigate: (path: string) => void } {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname))

  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = (path: string) => {
    window.history.pushState(null, '', path)
    setRoute(parsePath(path))
  }

  return { route, navigate }
}
